import React from 'react';

/**
 * Single source of truth for the streaming overlay themes.
 *
 * `supportsTitle` / `supportsBreaks` describe what the OVERLAY ACTUALLY RENDERS.
 * Verified against src/Pages/Streaming.js:
 *
 *   welshopen        -> renderYellowTvTheme()            reads display_breaks only
 *   world-grand-prix -> renderWorldGrandPrixTheme()      reads display_breaks only
 *   yushan-open      -> renderYushanOpenTheme()          reads display_breaks only
 *   classic-neon     -> renderNeonTheme()                reads display_breaks only
 *   broadcast        -> renderEnglishOpenScorebugTheme() has no break graphic
 *   neon             -> renderUkChampionshipTheme()      reads display_breaks only
 *   saudi-masters    -> renderSaudiMastersTheme()         reads display_breaks only
 *
 * `yellowtv` is retained only as a legacy stored-value alias for `welshopen`.
 * It is deliberately not exposed as a selectable style.
 *
 * The settings UI drives itself off these flags so it can never offer an option
 * the overlay throws away. If you add a theme, or change what one renders, update
 * it here as well as in Streaming.js - this file is the thing keeping the two in
 * agreement.
 */
export const STREAMING_THEMES = [
    /*
     * LEGACY WST STYLE TEMPORARILY DISABLED
     *
     * Keep the old WST renderer in Streaming.js for legacy links, but do not
     * expose it in the settings picker until its redesign is ready.
    {
        value: 'default',
        label: 'Legacy WST',
        name: 'WST In-House',
        hint: 'WST crimson rail with inner flags and a turquoise player marker',
        defaultNameFormat: 'full',
        supportsTitle: true,
        supportsBreaks: true,
        // Colours below are read from the theme's own CSS rather than guessed, so
        // the swatch is a likeness of what actually goes to air.
        preview: {
            bar: 'linear-gradient(180deg, #d7223a 0%, #c81832 100%)',
            cells: [
                { bg: '#d32236', fg: '#ffffff' },
                { bg: '#a91d36', fg: '#ffffff' },
                { bg: '#d32236', fg: '#ffffff' },
            ],
            lower: null,
            strip: '#d32236',
            stripWidth: '14%',
            width: '58%',
        },
    },
    */
    {
        value: 'welshopen',
        label: 'Style 1',
        name: 'Welsh Open 2025',
        hint: 'Full-width yellow rail with a teal match cell',
        defaultNameFormat: 'full',
        supportsTitle: false,
        supportsBreaks: true,
        preview: {
            bar: 'linear-gradient(90deg, #fcd10b 0%, #fcdf63 100%)',
            cells: [
                { bg: '#ffffff', fg: '#282828' },
                { bg: '#00857e', fg: '#ffffff' },
                { bg: '#ffffff', fg: '#282828' },
            ],
            lower: null,
            strip: null,
            width: '100%',
        },
    },
    {
        value: 'neon',
        label: 'Style 2',
        name: 'BBC / UK Championship',
        hint: 'Compact yellow rail with white points and a blue match cell',
        defaultNameFormat: 'full',
        supportsTitle: false,
        supportsBreaks: true,
        preview: {
            bar: '#f2d447',
            cells: [
                { bg: '#ffffff', fg: '#000000' },   /* .uk-points-st */
                { bg: '#4a70e8', fg: '#ffffff' },
                { bg: '#ffffff', fg: '#000000' },
            ],
            lower: null,
            strip: null,
            width: '50%',
        },
    },
    {
        value: 'yushan-open',
        label: 'Style 3',
        name: 'Yushan World Open 2026',
        hint: 'Compact midnight-blue rail with pale points, gold match numerals and a split break strip',
        defaultNameFormat: 'full',
        supportsTitle: false,
        supportsBreaks: true,
        preview: {
            bar: 'linear-gradient(180deg, #213b75 0%, #10234e 100%)',
            cells: [
                { bg: '#eceff1', fg: '#17264b' },
                { bg: '#294a91', fg: '#f1c94c' },
                { bg: '#eceff1', fg: '#17264b' },
            ],
            lower: '#10234e',
            lowerWidth: '28%',
            lowerAlign: 'flex-end',
            lowerMarginRight: '22%',
            strip: null,
            width: '56%',
            stage: '#15202b',
        },
    },
    {
        value: 'classic-neon',
        label: 'Style 4',
        name: 'Original Yellow',
        hint: 'Original full-width yellow rail with flags and grey information strip',
        defaultNameFormat: 'full',
        supportsTitle: false,
        supportsBreaks: true,
        preview: {
            bar: '#ffd234',
            cells: [
                { bg: '#ffffff', fg: '#000000' },
                { bg: '#345fee', fg: '#ffffff' },
                { bg: '#ffffff', fg: '#000000' },
            ],
            lower: '#4c4c4c',
            strip: null,
            width: '100%',
        },
    },
    {
        value: 'broadcast',
        label: 'Style 5',
        name: 'English Open 2025',
        hint: 'Translucent black rail with a red trapezoid score block',
        defaultNameFormat: 'full',
        supportsTitle: false,
        supportsBreaks: false,
        preview: {
            bar: 'rgba(7, 8, 9, .92)',
            cells: [
                { bg: '#f8f7ef', fg: '#202123' },
                { bg: '#db3039', fg: '#ffffff' },
                { bg: '#f8f7ef', fg: '#202123' },
            ],
            lower: null,
            strip: null,
            width: '100%',
            stage: '#172332',
        },
    },
    {
        value: 'world-grand-prix',
        label: 'Style 6',
        name: 'World Grand Prix 2024',
        hint: 'Black player rail with blue score cells and a dark active-player break extension',
        defaultNameFormat: 'full',
        supportsTitle: false,
        supportsBreaks: true,
        preview: {
            bar: 'linear-gradient(180deg, #11151c 0%, #03070c 100%)',
            cells: [
                { bg: '#17458e', fg: '#ffffff' },
                { bg: '#245fc9', fg: '#ffffff' },
                { bg: '#17458e', fg: '#ffffff' },
            ],
            lower: 'linear-gradient(180deg, #090e18 0%, #00030a 100%)',
            lowerWidth: '24%',
            strip: null,
            width: '70%',
            stage: '#172332',
        },
    },
    {
        value: 'saudi-masters',
        label: 'Style 7',
        name: 'Saudi Arabia Masters 2025',
        hint: 'Full-width black rail with angled green scores, a white match cell and player-aligned break ribbon',
        defaultNameFormat: 'full',
        supportsTitle: false,
        supportsBreaks: true,
        preview: {
            bar: 'linear-gradient(180deg, rgba(8,13,12,.98) 0%, rgba(3,6,5,.98) 100%)',
            cells: [
                { bg: '#15956f', fg: '#ffffff' },
                { bg: '#f2f2ed', fg: '#171a19' },
                { bg: '#15956f', fg: '#ffffff' },
            ],
            lower: '#f2f2ed',
            lowerWidth: '22.5%',
            lowerAlign: 'flex-end',
            lowerMarginRight: '15.6%',
            strip: null,
            width: '100%',
            stage: '#15202b',
        },
    },
];

export const getStreamingTheme = (value) =>
    STREAMING_THEMES.find((theme) => theme.value === value) ||
    STREAMING_THEMES.find((theme) => theme.value === 'welshopen') ||
    STREAMING_THEMES[0];

/** Themes that will actually put the tournament title on air. */
export const themesSupporting = (capability) =>
    STREAMING_THEMES.filter((theme) => theme[capability]).map((theme) => theme.label).join(' or ');

/**
 * A miniature of the overlay scorebar, drawn entirely from the `preview` block
 * above so that adding a theme never means touching this component. It is a
 * likeness rather than a rendering - enough for an operator to recognise the
 * style they want without reading the labels. Every preview uses a white stage
 * so dark and light scorebars are compared on the same neutral canvas.
 */
export const StreamingThemePreview = ({ theme }) => {
    const preview = theme.preview;
    const nameBar = { flex: 1, height: 4, borderRadius: 1, background: 'rgba(255,255,255,.85)' };

    return (
        <div
            style={{
                width: '100%',
                height: 46,
                borderRadius: 5,
                overflow: 'hidden',
                background: '#fff',
                border: '1px solid #e4e7ec',
                boxShadow: 'inset 0 1px 0 rgba(255,255,255,.9)',
                display: 'flex',
                flexDirection: 'column',
                justifyContent: 'center',
                alignItems: 'center',
            }}
        >
            {/* the bar itself: player names either side of the score cells */}
            <div
                style={{
                    width: preview.width || '88%',
                    display: 'flex',
                    alignItems: 'center',
                    gap: 2,
                    padding: '4px 4px',
                    background: preview.bar,
                }}
            >
                <div style={nameBar} />
                {preview.cells.map((cell, index) => (
                    <div
                        key={index}
                        style={{
                            width: 12,
                            height: 12,
                            background: cell.bg,
                            color: cell.fg,
                            fontSize: 7,
                            fontWeight: 700,
                            lineHeight: '12px',
                            textAlign: 'center',
                        }}
                    >
                        0
                    </div>
                ))}
                <div style={nameBar} />
            </div>

            {/* Optional lower rail used by legacy previews. */}
            {preview.lower && (
                <div
                    style={{
                        width: preview.lowerWidth || '88%',
                        height: 8,
                        background: preview.lower,
                        alignSelf: preview.lowerAlign || 'center',
                        marginRight: preview.lowerMarginRight || 0,
                        marginLeft: preview.lowerMarginLeft || 0,
                    }}
                />
            )}

            {/* The legacy WST narrow table/title tab remains documented here while disabled. */}
            {preview.strip && (
                <div style={{ width: preview.stripWidth || '30%', height: 8, background: preview.strip }} />
            )}
        </div>
    );
};
